import {Tooltip} from 'platform/components';
import {Text} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {MarketMetrics} from '../utils/computeMarketMetrics';
import {MetricsSkeleton} from './MetricsSkeleton';

const EMPTY_VALUE = '–';
const RATIO_DECIMALS = 1;

interface MarketBarsProps extends TestIdProps {
  metrics: MarketMetrics | null;
  isLoading: boolean;
  formatPrice: (price: number | null) => string;
}

/**
 * Supply × demand cell: “In advertisement” and “Sold in 90 days” mini bar rows with
 * counts and average prices, plus the supply/demand ratio badge and deltas.
 * Counts and Ø amounts live in their own grid columns so the numbers line up
 * vertically across the three rows (and across panel rows).
 */
export function MarketBars(props: MarketBarsProps) {
  const {metrics, isLoading, formatPrice} = props;

  const advertisedCount = metrics?.advertised.count ?? 0;
  const soldCount = metrics?.sold.count ?? 0;
  const barBase = Math.max(advertisedCount, soldCount, 1);

  const ratio = metrics?.supplyDemand.ratio ?? null;
  const demandExceedsSupply = metrics?.supplyDemand.demandExceedsSupply ?? false;
  const ratioLabel = isNotNil(ratio)
    ? i18n.t(
        demandExceedsSupply
          ? 'entity.filterForm.savedFiltersPanel.demandOverSupply'
          : 'entity.filterForm.savedFiltersPanel.supplyOverDemand',
        {ratio: ratio.toFixed(RATIO_DECIMALS)}
      )
    : null;

  const countDelta = metrics?.supplyDemand.countDelta ?? null;
  const priceDelta = metrics?.supplyDemand.averagePriceDelta ?? null;
  const deltaColor = isNotNil(countDelta) && countDelta < 0 ? 'danger' : 'success';

  const renderValueCells = (
    count: number,
    averagePrice: number | null,
    countTooltip: string,
    avgTooltip: string,
    testId: string
  ) =>
    isLoading ? (
      <SkeletonSpan>
        <MetricsSkeleton width="112px" height={4} />
      </SkeletonSpan>
    ) : (
      <>
        <CountCell>
          <Tooltip label={countTooltip} placement="top">
            <Text size="xSmall" alternative noWrap data-testid={suffixTestId(testId, props)}>
              {isNotNil(metrics) ? String(count) : EMPTY_VALUE}
            </Text>
          </Tooltip>
          {/* The dot slot always takes up space so counts align even in rows without an average */}
          <DotSlot $isVisible={isNotNil(metrics) && isNotNil(averagePrice)}>
            <Text size="xSmall" color="tertiary" noWrap>
              ·
            </Text>
          </DotSlot>
        </CountCell>
        <AvgCell>
          {isNotNil(metrics) && isNotNil(averagePrice) ? (
            <Tooltip label={avgTooltip} placement="top">
              <Text size="xSmall" alternative noWrap>
                {`Ø ${formatPrice(averagePrice)}`}
              </Text>
            </Tooltip>
          ) : null}
        </AvgCell>
      </>
    );

  return (
    <BarsGrid data-testid={suffixTestId('marketBars', props)}>
      <Text size="xSmall" color="secondary" noWrap>
        {i18n.t('entity.filterForm.savedFiltersPanel.advertised')}
      </Text>
      <BarTrack>
        <BarFill $variant="advertised" $percentage={(advertisedCount / barBase) * 100} />
      </BarTrack>
      {renderValueCells(
        advertisedCount,
        metrics?.advertised.averagePrice ?? null,
        i18n.t('entity.filterForm.savedFiltersPanel.tooltip.advertisedCount'),
        i18n.t('entity.filterForm.savedFiltersPanel.tooltip.advertisedAvg'),
        'advertised'
      )}

      <Text size="xSmall" color="secondary" noWrap>
        {i18n.t('entity.filterForm.savedFiltersPanel.sold90')}
      </Text>
      <BarTrack>
        <BarFill $variant="sold" $percentage={(soldCount / barBase) * 100} />
      </BarTrack>
      {renderValueCells(
        soldCount,
        metrics?.sold.averagePrice ?? null,
        i18n.t('entity.filterForm.savedFiltersPanel.tooltip.soldCount'),
        i18n.t('entity.filterForm.savedFiltersPanel.tooltip.soldAvg'),
        'sold'
      )}

      <RatioSlot>
        {isLoading ? (
          <MetricsSkeleton width="160px" height={4} />
        ) : isNotNil(ratioLabel) ? (
          <Tooltip
            label={i18n.t('entity.filterForm.savedFiltersPanel.tooltip.ratio')}
            placement="top"
          >
            <RatioBadge
              $demandExceedsSupply={demandExceedsSupply}
              data-testid={suffixTestId('ratioBadge', props)}
            >
              {demandExceedsSupply ? '▲' : '▼'} {ratioLabel}
            </RatioBadge>
          </Tooltip>
        ) : null}
      </RatioSlot>
      {isLoading ? (
        <SkeletonSpan>
          <MetricsSkeleton width="112px" height={4} />
        </SkeletonSpan>
      ) : (
        <>
          <CountCell>
            <Tooltip
              label={i18n.t('entity.filterForm.savedFiltersPanel.tooltip.deltaCount')}
              placement="top"
            >
              <Text
                size="xSmall"
                noWrap
                color={deltaColor}
                data-testid={suffixTestId('delta', props)}
              >
                {isNotNil(countDelta)
                  ? `${countDelta >= 0 ? '+' : '−'}${Math.abs(countDelta)}`
                  : EMPTY_VALUE}
              </Text>
            </Tooltip>
            <DotSlot $isVisible={isNotNil(priceDelta)}>
              <Text size="xSmall" noWrap color={deltaColor}>
                ·
              </Text>
            </DotSlot>
          </CountCell>
          <AvgCell>
            {isNotNil(priceDelta) ? (
              <Tooltip
                label={i18n.t('entity.filterForm.savedFiltersPanel.tooltip.deltaAvg')}
                placement="top"
              >
                <Text size="xSmall" noWrap color={deltaColor}>
                  {`Ø ${priceDelta >= 0 ? '+' : '−'}${formatPrice(Math.abs(priceDelta))}`}
                </Text>
              </Tooltip>
            ) : null}
          </AvgCell>
        </>
      )}
    </BarsGrid>
  );
}

const BarsGrid = styled.div`
  display: grid;
  /* Separate count and Ø-amount columns keep the numbers vertically aligned.
     The dot lives in the count cell so it stays glued to the number. */
  grid-template-columns:
    max-content minmax(${({theme}) => theme.getSize(20)}, ${({theme}) => theme.getSize(35)})
    max-content max-content;
  column-gap: ${({theme}) => theme.getSize(2)};
  row-gap: ${({theme}) => theme.getSize(1)};
  align-items: center;
  min-width: 0;

  /* Narrower desktops: shorter bar tracks so the panel columns fit side by side */
  @media ${({theme}) => theme.devices.notebook} {
    grid-template-columns:
      max-content minmax(${({theme}) => theme.getSize(12)}, ${({theme}) => theme.getSize(24)})
      max-content max-content;
  }

  /* Stacked layout: own full-width row, the bar track absorbs the leftover space */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-column: 1 / -1;
    grid-template-columns:
      max-content minmax(${({theme}) => theme.getSize(10)}, 1fr)
      max-content max-content;
  }
`;

const BarTrack = styled.div`
  height: ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
  overflow: hidden;
`;

const BarFill = styled.div<{$variant: 'advertised' | 'sold'; $percentage: number}>`
  height: 100%;
  border-radius: ${({theme}) => theme.radii.small};
  width: ${({$percentage}) => Math.max(0, Math.min(100, $percentage))}%;
  transition: width 0.3s ease;
  /* Matches the statistics charts: advertised cars are orange, sold ones dark grey */
  ${({theme, $variant}) =>
    $variant === 'advertised'
      ? css`
          background-color: ${theme.colors.palettes.orange[60][100]};
        `
      : css`
          background-color: ${theme.colors.palettes.neutral[300][100]};
        `}
`;

const CountCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: ${({theme}) => theme.getSize(1)};
  min-width: ${({theme}) => theme.getSize(10)};
`;

const AvgCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
`;

/** Invisible instead of unmounted — the dot keeps its width, so counts never shift. */
const DotSlot = styled.span<{$isVisible: boolean}>`
  visibility: ${({$isVisible}) => ($isVisible ? 'visible' : 'hidden')};
`;

const SkeletonSpan = styled.div`
  grid-column: 3 / 5;
  display: flex;
  justify-content: flex-end;
`;

const RatioSlot = styled.div`
  /* Spans label + bar so the badge width never widens the label column
     (which would shift bars in rows without a badge). */
  grid-column: 1 / 3;
  display: flex;
  align-items: center;
`;

const RatioBadge = styled.span<{$demandExceedsSupply: boolean}>`
  padding: ${({theme}) => `${theme.getSize(1)} ${theme.getSize(2)}`};
  border-radius: ${({theme}) => theme.radii.small};
  font-size: ${({theme}) => theme.fontSizes.text.xxSmall};
  font-weight: 600;
  white-space: nowrap;
  ${({theme, $demandExceedsSupply}) =>
    $demandExceedsSupply
      ? css`
          color: ${theme.colors.palettes.green[80][100]};
          background-color: ${theme.colors.palettes.green[20][100]};
        `
      : css`
          color: ${theme.colors.palettes.orange[80][100]};
          background-color: ${theme.colors.palettes.orange[20][100]};
        `}
`;
